import Cookies from "js-cookie"

import { MMB_SOURCE } from "../../consts/common"
import type { BookingId } from "../../types/primitiveTypes"
import type { DynamicRouteValues, MMB2FeatureRoute } from "./types"
import type { Features, Test } from "@kiwicom/darwin"
import { MockParams } from "@kiwicom/darwin/mock"
import { getAccessToken } from "@kiwicom/nitro/lib/services/auth"
import { isDarwinFeatureOn } from "@kiwicom/frontend-common/services/abTesting/darwinHelper"
import { isTequila } from "@kiwicom/nitro/lib/services/platform/shared"

export type MMB2URLProps = {
  bid: BookingId
  mmb2BasePath: string
  mmb2FeatureRoute: MMB2FeatureRoute
  language: string
  query: {
    baseApiUrl: string | null | undefined
    simpleToken: string | null | undefined
    platform: string | null | undefined
    iframe: string | null | undefined
    // Fix this the next time the file is edited. (Flow to TS)
    // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
    UTMs: {
      [key: string]: string
    }
    darwinFeatures: Features
    darwinTests: Test[]
    darwinWinners: Test[]
  }
  dynamicRouteValues?: DynamicRouteValues
}

/**
 * Combine all required data and return the redirect serach params to go to MMB2
 *
 * @param baseApiUrl - Set based on query param on page load of mmb1. Want to reset this query param in mmb2 for tequila, skymock etc
 * @param simpleToken - Query param for authentication when user is not logged in. Also stored in a cookie but adding here for cross origin testing.
 * @param platform - Query param for analytics. Indicates whether the user uses a browser or one of the mobile apps.
 * @param iframe - Query param for tequila. It is required to be able to display MMB2 iframe in tequila.
 * @param dynamicRouteValues - object with dynamic route values. Currently just using currency
 * @param darwinFeatures - object with features toggled in by Darwin
 * @param darwinTests - array of objects with tests toggled in by Darwin
 * @param darwinWinners - array of objects with winners toggled in by Darwin
 * @param UTMs - object with all UTM params and their values
 * @returns {string}
 */
export const createMMB2RedirectSearchParams = async ({
  query: {
    baseApiUrl,
    simpleToken,
    platform,
    iframe,
    UTMs,
    darwinFeatures,
    darwinTests,
    darwinWinners,
  },
  dynamicRouteValues,
}: Omit<
  MMB2URLProps,
  "bid" | "mmb2BasePath" | "mmb2FeatureRoute" | "language"
>): Promise<string> => {
  const searchParams = new URLSearchParams()

  const isTequilaEnabled = isTequila({ ui: Cookies.get("ui") ?? null })
  const isSkyUsersV2 = Boolean(
    isDarwinFeatureOn("USE_SKYUSERS_V2") || Cookies.get("dev_skyusers_v2_enabled"),
  )
  const accessToken = await getAccessToken({
    isSkyUsersV2: isTequilaEnabled ? false : isSkyUsersV2,
  })

  if (baseApiUrl) {
    searchParams.append("api_base", `${baseApiUrl}/mmb/v1/bff/web`)
    searchParams.append("api_base_payments", `${baseApiUrl}/payments/v1`)
  }

  if (simpleToken && !accessToken) {
    searchParams.append("simple_token", simpleToken)
  }

  // Query param for analytics. Source is a touch point where the user came from.
  searchParams.append("source", MMB_SOURCE)

  if (platform) {
    searchParams.append("platform", platform)
  }

  if (iframe) {
    searchParams.append("iframe", iframe)
  }

  if (dynamicRouteValues?.currency) {
    searchParams.append("currency", String(dynamicRouteValues.currency))
  }

  Object.keys(UTMs).forEach((utm) => {
    searchParams.append(utm, UTMs[utm])
  })

  Object.keys(darwinFeatures).forEach((feature) => {
    searchParams.append(
      darwinFeatures[feature] ? "darwin_mock_feature_on" : "darwin_mock_feature_off",
      feature,
    )
  })

  darwinTests.forEach(({ name, value }) => {
    searchParams.append(MockParams.MOCK_TEST, `${name}__${value}`)
  })

  darwinWinners.forEach(({ name, value }) => {
    searchParams.append(MockParams.MOCK_WINNER, `${name}__${value}`)
  })

  return searchParams.toString()
}

/**
 * Combine all required data and return the redirect link to go to MMB2
 *
 * @param bid - Booking id
 * @param mmb2BasePath - Base url of mmb2 application. Stored in redux and can be set in debug modal
 * @param mmb2FeatureRoute - Page route in mmb2, based on the modal that has been selected
 * @param language - Currently selected language in mmb1. Set to retain same language in mmb2
 * @param baseApiUrl - Set based on query param on page load of mmb1. Want to reset this query param in mmb2 for tequila, skymock etc
 * @param simpleToken - Query param for authentication when user is not logged in. Also stored in a cookie but adding here for cross origin testing.
 * @param platform - Query param for analytics. Indicates whether the user uses a browser or one of the mobile apps.
 * @param iframe - Query param for tequila. It is required to be able to display MMB2 iframe in tequila.
 * @param dynamicRouteValues - object with dynamic route values. Currently just using currency
 * @param darwinFeatures - object with features toggled in by Darwin
 * @param darwinTests - array of objects with tests toggled in by Darwin
 * @param darwinWinners - array of objects with winners toggled in by Darwin
 * @param UTMs - object with all UTM params and their values
 * @param ui - ui
 * @returns {string}
 */
const createMMB2RedirectLink = async ({
  bid,
  mmb2BasePath,
  mmb2FeatureRoute,
  language,
  ...props
}: MMB2URLProps): Promise<string> => {
  const mmbRedirectParams = await createMMB2RedirectSearchParams(props)

  return `${mmb2BasePath}/${language}/trips/${bid}/${mmb2FeatureRoute}?${mmbRedirectParams}`
}

export default createMMB2RedirectLink
